import React, { useEffect, useRef } from 'react';
import SceneItem from './SceneItem';
import AddScene from './AddScene';
import ScenesDraggingWrapper from './ScenesDraggingWrapper';
import { styled } from 'baseui';
import { lightTheme } from '@/customTheme';
import { hexToRgba } from '@/scenes/engine/utils/utils';
import { useScenesContext } from './ScenesProvider';

// Re-export sizing constants for backward compatibility (ThumbnailContent imports these)
export { MINIMUM_SIZE_PREVIEW, MAXIMUM_SIZE_PREVIEW } from './ScenesProvider';

const findItemNode = (list: HTMLElement | null, id: string) =>
  Array.from(list?.querySelectorAll<HTMLElement>('[data-scene-item-id]') ?? [])
    .find((el) => el.dataset.sceneItemId === id)

const getFocusTarget = (list: HTMLElement | null, id: string) =>
  findItemNode(list, id)?.querySelector<HTMLElement>('[data-scene-item-focus-target]')

const SceneWithIndicatorWrapper = styled('div', ({ $translate, $showIndicator, $indicatorPosition, $indicatorOffset, $isLast }: {
  $translate: 'up' | 'down' | 'none' | null,
  $showIndicator: boolean,
  $indicatorPosition: 'top' | 'bottom',
  $indicatorOffset: 'tight' | 'loose',
  $isLast: boolean,
}) => ({
  position: 'relative',
  display: 'flex',
  justifyContent: 'flex-start',
  transform: `translateY(${ $translate === 'up' ? '-6px' : $translate === 'down' ? '6px' : '0px' })`,
  transition: 'all 0.15s ease-out',
  backgroundColor: hexToRgba(lightTheme.colors.grayScale100, 0.6),
  borderRadius: '8px',
  marginBottom: $isLast && $showIndicator && $indicatorPosition === 'bottom' ? '10px' : '0px',
  '::before': { // drag indicator
    content: '""',
    height: '2px',
    background: '#FF0560',
    position: 'absolute',
    left: 0,
    right: 0,
    top: $indicatorPosition === 'top'
      ? ($indicatorOffset === 'tight' ? '-9px' : '-12px')
      : ($indicatorOffset === 'tight' ? 'calc(100% + 9px)' : 'calc(100% + 12px)'),
    zIndex: 1,
    borderRadius: '2px',
    opacity: $showIndicator ? 1 : 0,
  },
}))

// Pure UI component — all state and logic come from ScenesContext
const SceneList: React.FC = () => {
  const {
    items,
    selectedIds,
    hoveringId,
    setHoveringId,
    isActive,
    isContainerHovering,
    openEditMenuId,
    setOpenEditMenuId,
    openDownloadPopoverSceneId,
    isDragging,
    setIsDragging,
    sceneItemConfig,
    showAddButton,
    disable,
    baseSize,
    activeSize,
    defaultRatio,
    handleAddScene,
    handleSceneSelect,
    handleDownloadBadgeClick,
    handleDownloadPopoverOpenChange,
    getDownloadDestination,
    getDownloadSource,
    getItemSizes,
    calculateRotation,
    handleDragStart,
    handleDragEnd,
    onImageLoad,
    onVideoLoad,
    scrollSelectedIntoView,
    orientation,
  } = useScenesContext()
  const isHorizontal = orientation === 'horizontal'
  const [previousKey, nextKey] = isHorizontal ? ['ArrowLeft', 'ArrowRight'] : ['ArrowUp', 'ArrowDown']

  const listRef = useRef<HTMLDivElement>(null)
  const primarySelectedId = selectedIds[0]
  useEffect(() => {
    if (!scrollSelectedIntoView || !primarySelectedId) return
    findItemNode(listRef.current, primarySelectedId)?.scrollIntoView?.({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })
  }, [scrollSelectedIntoView, primarySelectedId])

  // Roving focus: the list is a single Tab stop (the selected item) and
  // Up / Down (Left / Right in a row) move the selection, with keyboard focus following it.
  const tabStopId = primarySelectedId ?? items[0]?.id
  // Selection can also change from outside (e.g. a stage's arrow keys); carry
  // focus along so Enter never re-selects the previously focused item.
  useEffect(() => {
    if (!primarySelectedId) return
    const focusedItem = (document.activeElement as HTMLElement | null)?.closest<HTMLElement>('[data-scene-item-id]')
    if (!focusedItem || !listRef.current?.contains(focusedItem) || focusedItem.dataset.sceneItemId === primarySelectedId) return
    getFocusTarget(listRef.current, primarySelectedId)?.focus({ preventScroll: true })
  }, [primarySelectedId])

  const handleListKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // A keyboard drag owns the arrow keys.
    if (isDragging || (e.key !== previousKey && e.key !== nextKey)) return
    const focusedId = (e.target as HTMLElement).closest<HTMLElement>('[data-scene-item-id]')?.dataset.sceneItemId
    if (!focusedId) return
    // Keep page-level arrow shortcuts (object nudge, stage navigation) from also running.
    e.preventDefault()
    e.stopPropagation()
    const next = items[items.findIndex((item) => item.id === focusedId) + (e.key === nextKey ? 1 : -1)]
    const target = next && getFocusTarget(listRef.current, next.id)
    if (!target) return
    target.focus({ preventScroll: true })
    // Select through the item's own click path, same as a pointer selection.
    target.click()
  }

  return (
    <ScenesDraggingWrapper
      items={items}
      selectedIds={selectedIds}
      setIsDragging={setIsDragging}
      baseSize={baseSize}
      activeSize={activeSize}
      ratioFrame={defaultRatio}
      calculateRotation={calculateRotation}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      getItemSizes={getItemSizes}
    >
      {(dragOverInformation) => (
        <div
          ref={listRef}
          onKeyDown={handleListKeyDown}
          style={{
            display: 'flex',
            flexDirection: isHorizontal ? 'row' : 'column',
            gap: isHorizontal ? '8px' : '12px',
            height: isHorizontal ? '100%' : 'auto',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: disable ? 'none' : 'all',
            width: isHorizontal ? 'auto' : '100%',
          }}
          id="scene-list"
        >
          {items.map((item, index) => {
            const itemSizes = getItemSizes(item)
            return (
              <SceneWithIndicatorWrapper
                key={item.id}
                data-scene-item-id={item.id}
                $translate={dragOverInformation?.shouldTranslate.find(i => i.index === index)?.translate || null}
                $showIndicator={dragOverInformation?.id === item.id}
                $indicatorPosition={dragOverInformation?.isAbove ? 'top' : 'bottom'}
                $indicatorOffset={dragOverInformation?.shouldTranslate[0]?.translate === 'none' ? 'tight' : 'loose'}
                $isLast={index === items.length - 1}
              >
                <SceneItem
                  item={item}
                  onSelect={handleSceneSelect}
                  selected={selectedIds.includes(item.id)}
                  isContainerHovering={isContainerHovering}
                  hoveringId={hoveringId}
                  setHoveringId={setHoveringId}
                  openEditMenuId={openEditMenuId}
                  setOpenEditMenuId={setOpenEditMenuId}
                  numberOfSelected={selectedIds.length}
                  order={index + 1}
                  isActive={isActive}
                  isDisabled={item.isDisabled || disable}
                  isLoading={item.isLoading}
                  isTabStop={item.id === tabStopId}
                  sceneItemConfig={sceneItemConfig}
                  downloadDestination={getDownloadDestination()}
                  downloadSource={getDownloadSource()}
                  baseSize={itemSizes.baseSize}
                  activeSize={itemSizes.activeSize}
                  onDownloadBadgeClick={handleDownloadBadgeClick}
                  isDownloadPopoverOpen={openDownloadPopoverSceneId === item.id}
                  onDownloadPopoverOpenChange={handleDownloadPopoverOpenChange}
                  onImageLoad={onImageLoad ? (e) => onImageLoad(item.id, e) : undefined}
                  onVideoLoad={onVideoLoad ? (e) => onVideoLoad(item.id, e) : undefined}
                />
              </SceneWithIndicatorWrapper>
            )
          })}
          {showAddButton && (
            <AddScene onAdd={handleAddScene} setHoveringId={setHoveringId} />
          )}
        </div>
      )}
    </ScenesDraggingWrapper>
  )
}

export default SceneList;

